/**
 * In-memory mock data layer — same function signatures as src/lib/api.ts,
 * so src/lib/dataSource.ts can swap between them with zero call-site
 * changes. Every screen in the app must work against this alone: it is the
 * default whenever no backend answers GET /api/chouette/catalog within 2s
 * (see dataSource.ts), which covers both local frontend-only development
 * and this prototype's very first run before the backend package exists.
 *
 * Seeded with the three reference stories from notes/rnd-chouette/ (moon
 * phases, hibernation/migration, light-years — see the plan's §0), plus one
 * "Les petits soucis du soir" collection story so mock mode has a card that
 * carries `universe` (docs/plans/2026-09-21-chouette-catalogue-v1.md), one
 * child (Léa, 6 ans), two packs, and a fake job that walks through
 * guard → writing → review → cover → voice over ~10s (see MOCK_PHASES
 * below), matching the real backend's weighted progress contract — cover
 * now comes before voice, so the cover is written to the story record, and
 * `result.cover_ready` published, partway through the job instead of at
 * the very end.
 */
import moonCover from "./covers/moon.svg?raw";
import hedgehogCover from "./covers/hedgehog.svg?raw";
import starsCover from "./covers/stars.svg?raw";
import { SILENT_AUDIO_DATA_URL } from "./silentAudio";
import { mockChapterSceneUrl, mockPackPortraitUrl } from "./chapterSceneImages";
import type {
  AiJob,
  ApiCallResult,
  AppearanceInput,
  Catalog,
  Child,
  ChildCreateInput,
  ChildInput,
  ChildSheet,
  ChildSheetsOut,
  ChouetteEventKind,
  CreateStoryBody,
  CreateStoryResponse,
  ListenEventKind,
  Me,
  PackCard,
  PairingConsumeResponse,
  PairingCreateResponse,
  StoryCard,
  StoryChapter,
  StoryDetail,
} from "../lib/types";
import { InsufficientCreditsError } from "../lib/api";
import { normalizePairingCode, PAIRING_CODE_MIN_LEN } from "../lib/pairingCode";

let idCounter = 1000;
function nextId(): string {
  idCounter += 1;
  return `mock-${idCounter}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** ISO datetime `days` ago from now — used to seed `published_at` so the
 *  mock mirrors the real backend's drip-publication contract without a
 *  fixed calendar date going stale. */
function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * DAY_MS).toISOString();
}

/** Mirrors the real backend: `is_new` is true for a story published within
 *  the last 7 days. */
function isNewStory(publishedAt: string | null | undefined): boolean {
  if (!publishedAt) return false;
  return Date.now() - new Date(publishedAt).getTime() <= 7 * DAY_MS;
}

interface MockStory extends StoryDetail {}

// The moon story is the one mock story with chapter illustrations (lot:
// chapter scenes player) — keeps the feature visible in mock/offline mode
// without a real image pipeline (see chapterSceneImages.ts). "Le vrai du
// faux" is left without one on purpose: it exercises the "keep the
// previous chapter's image" path for a real chapter, not just the
// pure-logic tests.
const CHAPTERS_MOON: StoryChapter[] = [
  {
    title: "La nuit où la lune a disparu",
    anchor: "ch1",
    start_s: 0,
    image_url: mockChapterSceneUrl("La lune a disparu", "#2a2249"),
    image_thumb_url: mockChapterSceneUrl("La lune a disparu", "#2a2249"),
  },
  {
    title: "Le premier morceau retrouvé",
    anchor: "ch2",
    start_s: 150,
    image_url: mockChapterSceneUrl("Un morceau retrouvé", "#1d1733"),
    image_thumb_url: mockChapterSceneUrl("Un morceau retrouvé", "#1d1733"),
  },
  {
    title: "Pleine lune",
    anchor: "ch3",
    start_s: 340,
    image_url: mockChapterSceneUrl("Pleine lune", "#3a2f5c"),
    image_thumb_url: mockChapterSceneUrl("Pleine lune", "#3a2f5c"),
  },
  { title: "Le vrai du faux", anchor: "vrai-du-faux", start_s: 560 },
];

const CHAPTERS_HEDGEHOG: StoryChapter[] = [
  { title: "Une drôle de peur", anchor: "ch1", start_s: 0 },
  { title: "Les amis qui partent", anchor: "ch2", start_s: 160 },
  { title: "Un terrier bien chaud", anchor: "ch3", start_s: 360 },
  { title: "Le vrai du faux", anchor: "vrai-du-faux", start_s: 580 },
];

const CHAPTERS_STARS: StoryChapter[] = [
  { title: "Le garçon qui n'aimait pas la nuit", anchor: "ch1", start_s: 0 },
  { title: "Un voyage de lumière", anchor: "ch2", start_s: 170 },
  { title: "Des étoiles déjà éteintes", anchor: "ch3", start_s: 380 },
  { title: "Le vrai du faux", anchor: "vrai-du-faux", start_s: 600 },
];

const CHAPTERS_CLICS: StoryChapter[] = [
  { title: "Trois derniers clics", anchor: "ch1", start_s: 0 },
  { title: "Le noir n'est pas vide", anchor: "ch2", start_s: 140 },
  { title: "Une lumière qui reste", anchor: "ch3", start_s: 300 },
  { title: "Le vrai du faux", anchor: "vrai-du-faux", start_s: 460 },
];

let stories: MockStory[] = [
  {
    id: "story-moon",
    slug: "la-lune-qui-perdait-des-morceaux",
    title: "La lune qui perdait des morceaux",
    synopsis:
      "Chaque nuit, la lune semble perdre un morceau. Une petite chouette part comprendre pourquoi, et découvre que rien n'est jamais vraiment perdu.",
    seed: "Les phases de la lune",
    age_min: 5,
    age_max: 9,
    duration_s: 11 * 60 + 20,
    cover_svg: moonCover,
    cover_colorway: "sun",
    is_free: true,
    pack_id: null,
    kind: "catalog",
    status: "ready",
    is_favorite: false,
    published_at: isoDaysAgo(30),
    audio_url: SILENT_AUDIO_DATA_URL,
    chapters: CHAPTERS_MOON,
    truth_check:
      "La lune ne perd jamais vraiment de morceaux : c'est l'ombre de la Terre et l'angle du soleil qui nous en cachent une partie, nuit après nuit. On appelle ça les phases de la lune, et ça recommence tous les 29 jours environ.",
    parent_note: null,
  },
  {
    id: "story-hedgehog",
    slug: "le-herisson-qui-avait-peur-de-l-hiver",
    title: "Le hérisson qui avait peur de l'hiver",
    synopsis:
      "Quand les feuilles tombent et que ses amis s'envolent vers le sud, un petit hérisson a peur d'être oublié. Il va apprendre qu'il a, lui aussi, sa façon de traverser l'hiver.",
    seed: "Hibernation et migration",
    age_min: 5,
    age_max: 8,
    duration_s: 10 * 60 + 40,
    cover_svg: hedgehogCover,
    cover_colorway: "sun",
    is_free: true,
    pack_id: null,
    kind: "catalog",
    status: "ready",
    is_favorite: true,
    published_at: isoDaysAgo(10),
    audio_url: SILENT_AUDIO_DATA_URL,
    chapters: CHAPTERS_HEDGEHOG,
    truth_check:
      "Certains animaux, comme le hérisson, hibernent : leur cœur ralentit et ils dorment tout l'hiver. D'autres, comme les hirondelles, migrent : ils s'envolent vers des pays plus chauds. Deux façons différentes de survivre au froid.",
    parent_note: null,
  },
  {
    id: "story-stars",
    slug: "le-garcon-qui-voulait-eteindre-les-etoiles",
    title: "Le garçon qui voulait éteindre les étoiles",
    synopsis:
      "Un garçon qui n'aime pas la nuit décide d'éteindre les étoiles une à une. Mais un vieux hibou lui révèle un secret : certaines sont éteintes depuis très longtemps déjà.",
    seed: "La lumière des étoiles met des années à nous parvenir",
    age_min: 6,
    age_max: 10,
    duration_s: 12 * 60 + 5,
    cover_svg: starsCover,
    cover_colorway: "lilas",
    is_free: true,
    pack_id: null,
    kind: "catalog",
    status: "ready",
    is_favorite: false,
    published_at: isoDaysAgo(1),
    audio_url: SILENT_AUDIO_DATA_URL,
    chapters: CHAPTERS_STARS,
    truth_check:
      "La lumière met du temps à voyager, même si elle va très vite. Certaines étoiles que l'on voit dans le ciel sont si loin que leur lumière a mis des centaines ou des milliers d'années à nous arriver. L'étoile a peut-être déjà changé, ou n'existe même plus.",
    parent_note: null,
  },
  {
    // A "Les petits soucis du soir" collection story (docs/plans/2026-09-21-
    // chouette-catalogue-v1.md in the memoneo repo) — the only mock entry
    // that carries `universe`, so mock mode has at least one card to
    // exercise it against. Slug follows the real seed pattern
    // (`slugify(title)-<universe-key>-<id>`) and `cover_colorway` is the
    // "soucis" family's pinned "lilas", matching what the real backend's
    // collection colorway table would assign — a mismatch here would be a
    // worse demo than no collection story at all.
    id: "story-clics",
    slug: "trois-clics-avant-de-dormir-soucis-001",
    title: "Trois clics avant de dormir",
    synopsis:
      "Chaque soir, un petit interrupteur a peur d'éteindre la dernière lumière. Il va découvrir que le noir garde toujours un peu de lumière avec lui.",
    seed: "La persistance rétinienne",
    age_min: 5,
    age_max: 8,
    duration_s: 9 * 60 + 30,
    cover_svg: moonCover,
    cover_colorway: "lilas",
    is_free: true,
    pack_id: null,
    kind: "catalog",
    status: "ready",
    is_favorite: false,
    universe: "soucis",
    audio_url: SILENT_AUDIO_DATA_URL,
    chapters: CHAPTERS_CLICS,
    truth_check:
      "Quand une lumière s'éteint, l'œil garde son image quelques instants : c'est la persistance rétinienne. Le noir complet n'arrive jamais tout de suite : il y a toujours un tout petit moment de lumière qui reste.",
    // The only mock story with a parent_note — a "Les petits soucis du
    // soir" (truth_mode "emotion") entry, where the note is mandatory on
    // the real backend. Gives mock mode at least one card to exercise the
    // "Pour vous, parents" section against.
    parent_note:
      "Beaucoup d'enfants ont du mal à lâcher la lumière avant de dormir, et c'est tout à fait normal. Ce soir, tu peux demander à ton enfant ce qu'il a préféré dans l'histoire, et essayer ensemble de compter les couleurs qui restent quand on ferme les yeux.",
  },
];

const packs: PackCard[] = [
  {
    id: "pack-animals",
    slug: "les-animaux-de-la-nuit",
    title: "Les animaux de la nuit",
    description: "Chouettes, renards, hérissons : des histoires pour apprivoiser la nuit.",
    cover_svg: hedgehogCover,
    cover_colorway: "coral",
    // The one pack with an approved group portrait; "Le ciel" below keeps
    // only its SVG, so mock mode exercises both sides of the fallback.
    cover_image_url: mockPackPortraitUrl("#5a2b3a"),
    cover_image_thumb_url: mockPackPortraitUrl("#5a2b3a"),
    price_credits: 12,
    unlocked: false,
    stories: [],
  },
  {
    id: "pack-sky",
    slug: "le-ciel",
    title: "Le ciel",
    description: "Lunes, étoiles et planètes, pour rêver les yeux fermés.",
    cover_svg: starsCover,
    cover_colorway: "sun",
    cover_image_url: null,
    cover_image_thumb_url: null,
    price_credits: 12,
    unlocked: false,
    stories: [],
  },
];

const SEED_CHILDREN: Child[] = [
  { id: "child-lea", first_name: "Léa", age: 6, likes: "les licornes, les histoires de pirates", companion: "Doudou le lapin" },
];
let children: Child[] = SEED_CHILDREN.map((c) => ({ ...c }));

let favorites = new Set<string>(stories.filter((s) => s.is_favorite).map((s) => s.id));
let unlockedPacks = new Set<string>();
/** Mirrors the real grid price of `chouette.story.generate` (3 credits). */
const STORY_COST_CREDITS = 3;

let credits = { credit_balance: 40, plan_balance: 20, pack_balance: 20 };
let kidPin: string | null = null;

/**
 * Mirrors the real backend's weighted progress contract (see
 * lib/jobProgress.ts's header comment): `progress_label` is one of these 5
 * keys, `progress_total` is always 100, and `progress_done` sits flat at a
 * step's `startPct` for its whole duration — except "voice", the longest
 * step, which the real backend interpolates continuously from chunk to
 * chunk. `endPct === startPct` everywhere but "voice" is what encodes
 * "flat" vs. "continuously interpolated" below; this is deliberately not
 * an idealised demo (every step ticking smoothly) — it's meant to
 * reproduce the exact shape of silence (writing: no event between 0.5s and
 * 3s) that made the real bug (comparing against French prose) invisible in
 * mock mode for so long. Note the order: "cover" sits before "voice",
 * matching the backend's 2026-09-20 revision.
 */
const MOCK_PHASES: { label: string; startS: number; endS: number; startPct: number; endPct: number }[] = [
  { label: "guard", startS: 0, endS: 0.5, startPct: 0, endPct: 0 },
  { label: "writing", startS: 0.5, endS: 3, startPct: 3, endPct: 3 },
  { label: "review", startS: 3, endS: 5, startPct: 28, endPct: 28 },
  { label: "cover", startS: 5, endS: 6, startPct: 40, endPct: 40 },
  { label: "voice", startS: 6, endS: 10, startPct: 48, endPct: 100 },
];

interface MockJob {
  job: AiJob;
  storyId: string;
  startedAt: number;
}
const jobs = new Map<string, MockJob>();

const COVER_PHASE_INDEX = MOCK_PHASES.findIndex((p) => p.label === "cover");
const MOCK_COVERS = [moonCover, hedgehogCover, starsCover];

function tickJob(mockJob: MockJob) {
  const elapsed = (Date.now() - mockJob.startedAt) / 1000;
  const lastPhase = MOCK_PHASES[MOCK_PHASES.length - 1];
  mockJob.job.progress_total = 100;
  const story = stories.find((s) => s.id === mockJob.storyId);

  if (elapsed >= lastPhase.endS) {
    mockJob.job.status = "done";
    mockJob.job.progress_label = lastPhase.label;
    mockJob.job.progress_done = 100;
    // The cover was already written to the story record — and cover_ready
    // already published — partway through the job (see below); this is a
    // fallback only, in case that never fired (e.g. a job resumed from a
    // state right at the boundary).
    if (story && !story.cover_svg) story.cover_svg = MOCK_COVERS[idCounter % MOCK_COVERS.length];
    mockJob.job.result = { story_id: mockJob.storyId, title: story?.title, cover_ready: true };
    if (story && story.status === "generating") {
      // Simulates the real pipeline's output (§4 of the plan) landing on
      // the story record — audio, chapters and synopsis arrive at the
      // "ready" transition; the cover already landed earlier (see below).
      story.status = "ready";
      story.audio_url = SILENT_AUDIO_DATA_URL;
      story.synopsis = `Une histoire sur mesure pour ${story.child?.first_name ?? "toi"}, autour de : ${story.seed}.`;
      story.chapters = [
        { title: "Le début de l'aventure", anchor: "ch1", start_s: 0 },
        { title: "Le vrai du faux", anchor: "vrai-du-faux", start_s: Math.max(60, story.duration_s - 90) },
      ];
      story.truth_check = `À propos de : ${story.seed}. (Texte de démonstration : la vraie relecture pédagogique vient du backend.)`;
    }
    return;
  }

  mockJob.job.status = "running";
  const phase = MOCK_PHASES.find((p) => elapsed < p.endS) ?? lastPhase;
  const phaseIndex = MOCK_PHASES.indexOf(phase);
  const span = phase.endS - phase.startS;
  const t = span > 0 ? Math.min(1, Math.max(0, (elapsed - phase.startS) / span)) : 0;
  mockJob.job.progress_label = phase.label;
  mockJob.job.progress_done = Math.round(phase.startPct + t * (phase.endPct - phase.startPct));

  // Mirrors the real backend: the story's id and title are published as a
  // partial `result` starting right before the "cover" step, once the
  // reread pass has settled on the title (see jobProgress.ts's
  // `stickyTitle` and Generation.tsx's useStickyTitle — this is what
  // exercises that code path in mock mode). Publishing it at the same
  // moment as the real backend, not earlier/later, is the point: a
  // mismatch here is exactly what let the SSE dead-code and the
  // French-prose progress bug both hide behind "it works in mock mode" for
  // as long as they did.
  if (story && phaseIndex >= COVER_PHASE_INDEX) {
    mockJob.job.result = { story_id: mockJob.storyId, title: story.title };
  }

  // Once the "cover" step itself has actually finished (not merely
  // started), simulate the pochette landing on the story record and
  // publish `cover_ready` — deliberately a separate moment from the title
  // above, and deliberately *not* at job-done: this is the shape the real
  // client is meant to react to (fetch the story once, mid-poll, well
  // before the job finishes), not something that only ever happens to
  // coincide with "done" in mock mode.
  if (story && elapsed >= MOCK_PHASES[COVER_PHASE_INDEX].endS) {
    if (!story.cover_svg) story.cover_svg = MOCK_COVERS[idCounter % MOCK_COVERS.length];
    mockJob.job.result = { ...mockJob.job.result, story_id: mockJob.storyId, cover_ready: true };
  }
}

function cloneStory(s: MockStory): StoryDetail {
  return { ...s, is_favorite: favorites.has(s.id), is_new: isNewStory(s.published_at) };
}

function toCard(s: MockStory): StoryCard {
  const {
    id, slug, title, synopsis, seed, age_min, age_max, duration_s, cover_svg,
    is_free, pack_id, kind, status, is_favorite, fail_reason, job_id, child, universe,
    published_at, is_new,
  } = cloneStory(s);
  return {
    id, slug, title, synopsis, seed, age_min, age_max, duration_s, cover_svg,
    is_free, pack_id, kind, status, is_favorite, fail_reason, job_id, child, universe,
    published_at, is_new,
  };
}

/** Deterministic per day (and, when a child is given, per account): picks
 *  among stories matching that child's age band when at least one does,
 *  otherwise among the whole free list. Mirrors the real backend's "story
 *  of the evening" — same index every time `mockGetCatalog` is called on a
 *  given calendar day. */
function pickTonight(free: StoryCard[], childId?: string): StoryCard | null {
  if (free.length === 0) return null;
  let candidates = free;
  if (childId) {
    const child = children.find((c) => c.id === childId);
    if (child) {
      const forAge = free.filter((s) => s.age_min <= child.age && child.age <= s.age_max);
      if (forAge.length > 0) candidates = forAge;
    }
  }
  const dayIndex = new Date().getDate();
  return candidates[dayIndex % candidates.length];
}

export function mockGetCatalog(childId?: string): Promise<Catalog> {
  const free = stories
    .filter((s) => s.is_free && s.status !== "hidden")
    .map(toCard)
    .sort((a, b) => (b.published_at ?? "").localeCompare(a.published_at ?? ""));
  const packCards: PackCard[] = packs.map((p) => ({ ...p, unlocked: unlockedPacks.has(p.id) }));
  return Promise.resolve({
    free,
    packs: packCards,
    tonight: pickTonight(free, childId),
    new_since: isoDaysAgo(7),
  });
}

export function mockGetStory(id: string): Promise<StoryDetail> {
  const s = stories.find((x) => x.id === id || x.slug === id);
  if (!s) return Promise.reject(new Error("not_found"));
  return Promise.resolve(cloneStory(s));
}

export function mockGetMyStories(childId?: string): Promise<StoryCard[]> {
  const mine = stories.filter((s) => s.kind === "custom" && (!childId || s.child?.id === childId));
  return Promise.resolve(mine.map(toCard).sort((a, b) => b.id.localeCompare(a.id)));
}

export function mockCreateStory(body: CreateStoryBody): Promise<CreateStoryResponse> {
  const cost = STORY_COST_CREDITS;
  if (credits.credit_balance < cost) {
    return Promise.reject(new InsufficientCreditsError(cost, credits.credit_balance));
  }
  credits = { ...credits, credit_balance: credits.credit_balance - cost };
  const child = children.find((c) => c.id === body.child_id);
  const storyId = nextId();
  const jobId = nextId();
  const title = `Une histoire pour ${child?.first_name ?? "toi"}`;
  const story: MockStory = {
    id: storyId,
    slug: storyId,
    title,
    synopsis: "On écrit encore les premières lignes…",
    seed: body.seed || "Une graine adaptée à l'univers choisi",
    age_min: Math.max(3, (child?.age ?? 6) - 1),
    age_max: (child?.age ?? 6) + 2,
    duration_s: body.duration_min * 60,
    cover_svg: null,
    is_free: false,
    pack_id: null,
    kind: "custom",
    status: "generating",
    is_favorite: false,
    fail_reason: null,
    job_id: jobId,
    child: child ? { id: child.id, first_name: child.first_name } : null,
    audio_url: null,
    chapters: [],
    truth_check: null,
    parent_note: null,
  };
  stories = [story, ...stories];
  jobs.set(jobId, {
    job: {
      id: jobId,
      status: "pending",
      progress_done: 0,
      progress_total: 100,
      progress_label: "guard",
    },
    storyId,
    startedAt: Date.now(),
  });
  return Promise.resolve({ story_id: storyId, job_id: jobId });
}

export function mockGetAiJob(jobId: string): Promise<AiJob> {
  const mockJob = jobs.get(jobId);
  if (!mockJob) return Promise.reject(new Error("not_found"));
  tickJob(mockJob);
  return Promise.resolve({ ...mockJob.job });
}

export function mockSetFavorite(storyId: string, favorite: boolean): Promise<void> {
  if (favorite) favorites.add(storyId);
  else favorites.delete(storyId);
  const s = stories.find((x) => x.id === storyId);
  if (s) s.is_favorite = favorite;
  return Promise.resolve();
}

export function mockDeleteStory(storyId: string): Promise<void> {
  stories = stories.filter((s) => s.id !== storyId);
  return Promise.resolve();
}

export function mockListChildren(): Promise<Child[]> {
  return Promise.resolve(children.map(withSheet));
}

export function mockCreateChild(input: ChildCreateInput): Promise<Child> {
  if (!input.parental_attestation) return Promise.reject(new Error("parental_attestation_required"));
  const { parental_attestation: _attested, ...fields } = input;
  const child: Child = { id: nextId(), ...fields, appearance: null, sheet: null };
  children = [...children, child];
  return Promise.resolve(withSheet(child));
}

export function mockPatchChild(id: string, input: Partial<ChildInput>): Promise<Child> {
  children = children.map((c) => (c.id === id ? { ...c, ...input } : c));
  const child = children.find((c) => c.id === id);
  if (!child) return Promise.reject(new Error("not_found"));
  return Promise.resolve(withSheet(child));
}

export function mockDeleteChild(id: string): Promise<void> {
  children = children.filter((c) => c.id !== id);
  // Mirrors the backend (plan D3): the appearance and every sheet go with
  // the child.
  sheetsByChild.delete(id);
  sheetRequestsByChild.delete(id);
  return Promise.resolve();
}

// ── Child appearance and reference sheets ──────────────────────────────────
// docs/plans/2026-09-27-chouette-planche-enfant.md, lot 2. Mirrors the real
// router: a request "renders" for MOCK_SHEET_REQUEST_MS, then yields exactly
// one candidate sheet, five requests allowed per appearance,
// `matches_appearance` keyed on the appearance plus the age.
const MOCK_SHEET_REQUEST_MS = 6000;
const MOCK_MAX_REQUESTS_PER_APPEARANCE = 5;
/** Words the mock refuses in any of the three free-text fields (doudou
 *  detail, doudou free type, child detail), to exercise the
 *  `*_refused`/`*_missing` paths without a model. */
const MOCK_REFUSED_TEXT = /(écri|nom|marque|sang|peur)/i;

interface MockSheet extends ChildSheet {
  appearanceKey: string;
}
interface MockRequest {
  appearanceKey: string;
  startedAt: number;
  /** Version the sheet this request produces will carry. */
  version: number;
  resolved: boolean;
}
let sheetsByChild = new Map<string, MockSheet[]>();
let sheetRequestsByChild = new Map<string, MockRequest[]>();

function appearanceKey(child: Child): string | null {
  if (!child.appearance) return null;
  const { companion_detail_en: _en, ...rest } = child.appearance;
  return JSON.stringify({
    age: child.age,
    ...rest,
    companion_detail: rest.companion_detail.trim(),
    companion_kind_other: (rest.companion_kind_other ?? "").trim(),
    child_detail: (rest.child_detail ?? "").trim(),
  });
}

function settleRequests(childId: string): void {
  const requests = sheetRequestsByChild.get(childId) ?? [];
  const list = sheetsByChild.get(childId) ?? [];
  for (const request of requests) {
    if (request.resolved || Date.now() - request.startedAt < MOCK_SHEET_REQUEST_MS) continue;
    request.resolved = true;
    list.push({
      id: nextId(), version: request.version, status: "candidate", matches_appearance: true,
      created_at: new Date().toISOString(), appearanceKey: request.appearanceKey,
    });
  }
  sheetsByChild.set(childId, list);
}

function withSheet(child: Child): Child {
  settleRequests(child.id);
  const key = appearanceKey(child);
  const chosen = (sheetsByChild.get(child.id) ?? []).find((s) => s.status === "chosen");
  return {
    ...child,
    appearance: child.appearance ?? null,
    sheet: chosen ? { id: chosen.id, version: chosen.version, matches_appearance: chosen.appearanceKey === key } : null,
  };
}

function sheetsOut(child: Child): ChildSheetsOut {
  settleRequests(child.id);
  const key = appearanceKey(child);
  const requests = sheetRequestsByChild.get(child.id) ?? [];
  const used = key ? requests.filter((r) => r.appearanceKey === key).length : 0;
  return {
    generating: requests.some((r) => !r.resolved),
    batches_left: key ? Math.max(0, MOCK_MAX_REQUESTS_PER_APPEARANCE - used) : 0,
    sheets: (sheetsByChild.get(child.id) ?? [])
      .slice()
      .sort((a, b) => b.version - a.version)
      .map(({ appearanceKey: sheetKey, ...rest }) => ({ ...rest, matches_appearance: sheetKey === key })),
  };
}

function findChild(id: string): Child | undefined {
  return children.find((c) => c.id === id);
}

function httpError(detail: string): Error {
  return new Error(detail);
}

export function mockPutChildAppearance(childId: string, body: AppearanceInput): Promise<Child> {
  const child = findChild(childId);
  if (!child) return Promise.reject(httpError("No such child."));
  const detail = body.companion_detail.trim();
  const kindOther = (body.companion_kind_other ?? "").trim();
  const childDetail = (body.child_detail ?? "").trim();
  if (body.companion_kind === "other" && !kindOther) {
    return Promise.reject(httpError("companion_kind_other_missing"));
  }
  if (detail && MOCK_REFUSED_TEXT.test(detail)) return Promise.reject(httpError("companion_detail_refused"));
  if (kindOther && MOCK_REFUSED_TEXT.test(kindOther)) return Promise.reject(httpError("companion_kind_refused"));
  if (childDetail && MOCK_REFUSED_TEXT.test(childDetail)) return Promise.reject(httpError("child_detail_refused"));
  const updated: Child = {
    ...child,
    appearance: {
      ...body,
      companion_detail: detail,
      companion_detail_en: detail,
      companion_kind_other: body.companion_kind === "other" ? kindOther : "",
      child_detail: childDetail,
    },
  };
  children = children.map((c) => (c.id === childId ? updated : c));
  return Promise.resolve(withSheet(updated));
}

export function mockStartChildSheets(childId: string): Promise<ChildSheetsOut> {
  const child = findChild(childId);
  if (!child) return Promise.reject(httpError("No such child."));
  const key = appearanceKey(child);
  if (!key) return Promise.reject(httpError("appearance_missing"));
  const current = sheetsOut(child);
  if (current.generating) return Promise.reject(httpError("sheets_already_generating"));
  if (current.batches_left <= 0) return Promise.reject(httpError("sheet_batches_exhausted"));
  const requests = sheetRequestsByChild.get(childId) ?? [];
  const version = (sheetsByChild.get(childId) ?? []).reduce((max, s) => Math.max(max, s.version), 0) + 1;
  requests.push({ appearanceKey: key, startedAt: Date.now(), version, resolved: false });
  sheetRequestsByChild.set(childId, requests);
  return Promise.resolve(sheetsOut(child));
}

export function mockGetChildSheets(childId: string): Promise<ChildSheetsOut> {
  const child = findChild(childId);
  if (!child) return Promise.reject(httpError("No such child."));
  return Promise.resolve(sheetsOut(child));
}

export function mockChooseChildSheet(childId: string, sheetId: string): Promise<Child> {
  const child = findChild(childId);
  if (!child) return Promise.reject(httpError("No such child."));
  settleRequests(childId);
  const list = sheetsByChild.get(childId) ?? [];
  const target = list.find((s) => s.id === sheetId);
  if (!target) return Promise.reject(httpError("No such sheet."));
  for (const s of list) {
    if (s.status === "chosen") s.status = "candidate";
  }
  target.status = "chosen";
  return Promise.resolve(withSheet(child));
}

const MOCK_PYJAMAS = ["#a78bfa", "#7fd8c0", "#ff7a6b", "#ffce5c"];

/** A stand-in for the WebP preview with the real sheet's layout: 1536x1024,
 *  the child three times (front, three-quarter, profile) then the doudou
 *  twice, on one ground line. The pyjama colour changes with the version so
 *  candidates don't look identical. Enough to check crops in mock mode. */
function mockSheetSvg(version: number): string {
  const pyjama = MOCK_PYJAMAS[version % MOCK_PYJAMAS.length];
  const child = (cx: number, squash: number) => `
    <g transform="translate(${cx} 0) scale(${squash} 1)">
      <circle cx="0" cy="330" r="95" fill="#eec9a5"/>
      <path d="M-100 300 Q0 180 100 300 L100 260 Q0 150 -100 260 Z" fill="#7a4a2b"/>
      <rect x="-95" y="430" width="190" height="260" rx="60" fill="${pyjama}"/>
      <rect x="-80" y="680" width="65" height="180" rx="30" fill="${pyjama}"/>
      <rect x="15" y="680" width="65" height="180" rx="30" fill="${pyjama}"/>
    </g>`;
  const toy = (cx: number) => `
    <g transform="translate(${cx} 0)">
      <ellipse cx="-35" cy="560" rx="22" ry="70" fill="#f4efe6"/>
      <ellipse cx="35" cy="560" rx="22" ry="70" fill="#f4efe6"/>
      <circle cx="0" cy="660" r="70" fill="#f4efe6"/>
      <ellipse cx="0" cy="790" rx="85" ry="80" fill="#f4efe6"/>
    </g>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1536 1024" width="1536" height="1024">
    <rect width="1536" height="1024" fill="#1d1733"/>
    ${child(154, 1)}${child(461, 0.8)}${child(768, 0.55)}${toy(1075)}${toy(1382)}
    <line x1="0" y1="870" x2="1536" y2="870" stroke="#3c3363" stroke-width="4"/>
  </svg>`;
}

export function mockGetChildSheetImageBlob(childId: string, sheetId: string): Promise<Blob> {
  const sheet = (sheetsByChild.get(childId) ?? []).find((s) => s.id === sheetId);
  if (!sheet) return Promise.reject(httpError("No such sheet."));
  return Promise.resolve(new Blob([mockSheetSvg(sheet.version)], { type: "image/svg+xml" }));
}

/** A stand-in for the real 256x256 portrait endpoint: just the front pose's
 *  face, already square and centred — no CSS crop needed on top, mirroring
 *  what the real backend now serves. */
function mockPortraitSvg(version: number): string {
  const pyjama = MOCK_PYJAMAS[version % MOCK_PYJAMAS.length];
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="256" height="256">
    <rect width="256" height="256" fill="#1d1733"/>
    <circle cx="128" cy="150" r="70" fill="#eec9a5"/>
    <path d="M53 130 Q128 40 203 130 L203 95 Q128 20 53 95 Z" fill="#7a4a2b"/>
    <rect x="83" y="205" width="90" height="60" rx="20" fill="${pyjama}"/>
  </svg>`;
}

export function mockGetChildSheetPortraitBlob(childId: string, sheetId: string): Promise<Blob> {
  const sheet = (sheetsByChild.get(childId) ?? []).find((s) => s.id === sheetId);
  if (!sheet) return Promise.reject(httpError("No such sheet."));
  return Promise.resolve(new Blob([mockPortraitSvg(sheet.version)], { type: "image/svg+xml" }));
}

export function mockGetMe(): Promise<Me> {
  // The mock account is a Chouette+ subscriber — the one persona that
  // exercises every branch this lot added (the settings counter, the kid
  // mode gate, the "8 per month" wording) without a second backend account
  // to flip between. The period mirrors the display example from the
  // offer's cadrage (D8): 3 stories written out of 8, from the 12th of a
  // month to the 11th of the next.
  const now = new Date();
  const periodStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 12));
  if (periodStart.getTime() > now.getTime()) periodStart.setUTCMonth(periodStart.getUTCMonth() - 1);
  const periodEnd = new Date(Date.UTC(periodStart.getUTCFullYear(), periodStart.getUTCMonth() + 1, 11));
  const storiesPerPeriod = 8;
  const storiesUsedThisPeriod = 3;

  return Promise.resolve({
    ...credits,
    kid_pin_set: kidPin !== null,
    plan_code: "CHOUETTE_PLUS",
    is_plus: true,
    story_cost_credits: STORY_COST_CREDITS,
    stories_left_this_period: storiesPerPeriod - storiesUsedThisPeriod,
    stories_left_total: Math.floor(credits.credit_balance / STORY_COST_CREDITS),
    period_start: periodStart.toISOString(),
    period_end: periodEnd.toISOString(),
    stories_per_period: storiesPerPeriod,
    stories_used_this_period: storiesUsedThisPeriod,
  });
}

export function mockSetKidPin(pin: string): Promise<void> {
  kidPin = pin;
  return Promise.resolve();
}

/** En démo, rien à effacer : la suppression aboutit, c'est tout. */
export function mockDeleteAccount(_confirmEmail: string): Promise<{ deleted: boolean }> {
  return Promise.resolve({ deleted: true });
}

export function mockVerifyKidPin(pin: string): Promise<{ ok: boolean }> {
  return Promise.resolve({ ok: kidPin !== null && pin === kidPin });
}

/** Le mail de réinitialisation n'existe pas en démo : la demande aboutit, c'est tout. */
export function mockRequestPinReset(): Promise<void> {
  return Promise.resolve();
}

/** Tout jeton non vide efface le code, sauf « bad » qui simule un lien
 *  expiré ou déjà utilisé (400 `invalid_or_expired_token` côté backend). */
export function mockResetPin(token: string): Promise<void> {
  if (!token || token === "bad") return Promise.reject(new Error("invalid_or_expired_token"));
  kidPin = null;
  return Promise.resolve();
}

export function mockUnlockPack(id: string): Promise<void> {
  const pack = packs.find((p) => p.id === id);
  if (!pack) return Promise.reject(new Error("not_found"));
  if (credits.credit_balance < pack.price_credits) {
    return Promise.reject(new InsufficientCreditsError(pack.price_credits, credits.credit_balance));
  }
  credits = { ...credits, credit_balance: credits.credit_balance - pack.price_credits };
  unlockedPacks.add(id);
  return Promise.resolve();
}

// ── Device pairing (login by code) ─────────────────────────────────────────
// Real codes are 8 chars (memoneo's pairing.py); the mock keeps it simple:
// one fixed 6-char fake code is always shown by mockPairingCreate, and
// mockPairingConsume accepts *any* code of at least PAIRING_CODE_MIN_LEN
// characters (normalized) — no need to actually copy the fake code between
// two mock sessions/tabs to exercise the flow.
const MOCK_PAIRING_CODE = "DEMO12";
const MOCK_PAIRING_TTL_MS = 5 * 60 * 1000;
const MOCK_PAIRING_USER_ID = "mock-owner";

/** A JWT-shaped (but unsigned) token so `decodeJwtSub` can still read a
 *  `sub` claim out of it, same as a real device JWT would carry. */
function fakeDeviceJwt(sub: string): string {
  const b64 = (obj: unknown) =>
    btoa(JSON.stringify(obj)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  const exp = Math.floor(Date.now() / 1000) + 90 * 24 * 60 * 60;
  return `${b64({ alg: "none", typ: "JWT" })}.${b64({ sub, exp })}.mock-signature`;
}

export function mockPairingCreate(_targetUserId: string): Promise<PairingCreateResponse> {
  return Promise.resolve({
    code: MOCK_PAIRING_CODE,
    expires_at: new Date(Date.now() + MOCK_PAIRING_TTL_MS).toISOString(),
    qr_payload: `https://chouette.memoneo.app/login/code?code=${MOCK_PAIRING_CODE}`,
  });
}

export function mockPairingConsume(code: string, _deviceLabel?: string): Promise<PairingConsumeResponse> {
  if (normalizePairingCode(code).length < PAIRING_CODE_MIN_LEN) {
    return Promise.reject(new Error("invalid_code"));
  }
  return Promise.resolve({
    token: fakeDeviceJwt(MOCK_PAIRING_USER_ID),
    user_id: MOCK_PAIRING_USER_ID,
    display_name: "Léa",
  });
}

// ── Listens and pilotage events (plan Lot C.2) — mock mode does nothing but
// log: there is no admin funnel to reflect it into here, and the front's own
// dedup/retry logic (listenQueue.ts, ChouettePlus.tsx) is exercised the same
// way against mock or real. ─────────────────────────────────────────────────

export function mockPostListen(storyId: string, event: ListenEventKind, at: string): Promise<ApiCallResult> {
  console.info(`[mock] listen ${event}`, { storyId, at });
  return Promise.resolve({ status: 204 });
}

export function mockPostEvent(kind: ChouetteEventKind, meta: Record<string, unknown>): Promise<ApiCallResult> {
  console.info(`[mock] event ${kind}`, meta);
  return Promise.resolve({ status: 204 });
}

/** Test-only: resets every mutable slice of mock state to its seeded value. */
export function __resetMockStateForTests(): void {
  favorites = new Set(["story-hedgehog"]);
  unlockedPacks = new Set();
  credits = { credit_balance: 40, plan_balance: 20, pack_balance: 20 };
  kidPin = null;
  jobs.clear();
  sheetsByChild = new Map();
  sheetRequestsByChild = new Map();
  children = SEED_CHILDREN.map((c) => ({ ...c }));
}
